import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import style from './Milestones.module.css'

// Drop venture_cup_winner.jpeg into src/assets/images/
let ventureImg = null
try { ventureImg = require('../../../assets/images/venture_cup_winner.jpeg') } catch (_) {}

const BADGE = {
  launch:  { label: 'Launch',   bg: 'rgba(67,44,91,0.1)',    color: '#432C5B' },
  award:   { label: 'Award',    bg: 'rgba(180,83,9,0.1)',    color: '#b45309' },
  program: { label: 'Program',  bg: 'rgba(29,78,216,0.1)',   color: '#1d4ed8' },
  grant:   { label: 'Grant',    bg: 'rgba(4,120,87,0.1)',    color: '#047857' },
}

// Most recent first
const MILESTONES = [
  {
    date: 'Apr 2026',
    type: 'launch',
    title: 'Discovery Universe',
    desc: 'Opportunities mapped as a visual universe clustered by industry, technology, and fit. See your entire career landscape at once.',
  },
  {
    date: 'Feb 2026',
    type: 'launch',
    title: 'Weekly Shortlist',
    desc: '20 curated opportunities every week ranked, scored, and delivered with a clear "why this, why now" and one action to take.',
  },
  {
    date: 'Feb 2026',
    type: 'grant',
    title: 'Vinnova Verification Grant II',
    desc: "Secured a second verification grant from Vinnova, continuing to scale Chimp's AI-driven career intelligence platform.",
  },
  {
    date: 'Dec 2025',
    type: 'award',
    title: 'Venture Cup IdeaHunt Winner',
    desc: 'Won Venture Cup IdeaHunt 2025 Region East competing against hundreds of startups across Sweden.',
    img: ventureImg,
  },
  {
    date: 'Nov 2025',
    type: 'launch',
    title: 'Job Fit Check',
    desc: "Drop any job listing, get an instant fit score. Know what you match, what you're missing, and exactly what to do next before you apply.",
  },
  {
    date: 'Sep 2025',
    type: 'launch',
    title: 'CV Analyzer',
    desc: 'Launched AI-powered CV analysis a real score, skill gap breakdown, and role fit matched to 45,000 companies in Sweden.',
  },
  {
    date: 'Jun 2025',
    type: 'program',
    title: 'Google for Startups',
    desc: "Selected for the Google for Startups program, gaining cloud credits, mentorship, and access to Google's global startup network.",
  },
  {
    date: 'Feb 2025',
    type: 'launch',
    title: 'Feed 1.0 Goes Live',
    desc: "Shipped Chimp's first product a curated stream of jobs and opportunities matched to your profile.",
  },
  {
    date: 'Sep 2023',
    type: 'grant',
    title: 'Vinnova Verification Grant I',
    desc: 'Received our first verification grant from Vinnova validating the Chimp concept and funding early-stage development.',
  },
]

const INITIAL_COUNT = 4

const TimelineCard = ({ m, index }) => {
  const isRight = index % 2 !== 0
  const badge = BADGE[m.type]
  return (
    <motion.div
      className={`${style.item} ${isRight ? style.itemRight : style.itemLeft}`}
      initial={{ opacity: 0, x: isRight ? 44 : -44 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.48, ease: 'easeOut' }}
    >
      <div className={style.card}>
        <div className={style.cardMeta}>
          <span className={style.badge} style={{ background: badge.bg, color: badge.color }}>
            {badge.label}
          </span>
          <span className={style.date}>{m.date}</span>
        </div>
        <h3 className={style.cardTitle}>{m.title}</h3>
        <p className={style.cardDesc}>{m.desc}</p>
        {m.img && (
          <div className={style.imgWrap}>
            <img src={m.img} alt={m.title} className={style.img} />
          </div>
        )}
      </div>
    </motion.div>
  )
}

const Milestones = () => {
  const [showAll, setShowAll] = useState(false)

  return (
    <section className={style.section} id="milestones">
      <motion.div
        className={style.header}
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.55 }}
      >
        <span className={style.eyebrow}>Our Journey</span>
        <h2 className={style.title}>From idea to impact.</h2>
        <p className={style.subtitle}>
          Grants, launches, and recognition every step from first concept to live platform.
        </p>
      </motion.div>

      <div className={style.timeline}>
        {/* Always-visible: 4 most recent */}
        {MILESTONES.slice(0, INITIAL_COUNT).map((m, i) => (
          <TimelineCard key={m.title} m={m} index={i} />
        ))}

        {/* Older items revealed on demand */}
        <AnimatePresence>
          {showAll && MILESTONES.slice(INITIAL_COUNT).map((m, i) => (
            <motion.div
              key={m.title}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.4, delay: i * 0.08, ease: 'easeOut' }}
            >
              <TimelineCard m={m} index={INITIAL_COUNT + i} />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Show more / less button */}
      <motion.div
        className={style.expandRow}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4 }}
      >
        <motion.button
          className={style.expandBtn}
          onClick={() => setShowAll(v => !v)}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
        >
          {showAll ? 'Show less ↑' : `Show older milestones ↓`}
        </motion.button>
      </motion.div>
    </section>
  )
}

export default Milestones
